import { useState, useCallback, useEffect, useMemo } from 'react';
import { useShopContext } from '../context/ShopContext';

// ============================================
// 📦 TYPES
// ============================================

export type SortOption =
  | 'price_low'
  | 'price_high'
  | 'newest'
  | 'popular'
  | 'featured';

export interface ProductFilterState {
  sort?: SortOption;
  category?: string;
  store?: string;
  search?: string;
  price_min?: number;
  price_max?: number;
  branch?: string; // injected internally
}

export interface ProductFilterPayload {
  sort?: SortOption;
  category?: string;
  store?: string;
  search?: string;
  price_min?: number;
  price_max?: number;
  branch: string;
}

export type FilterMode = 'replace' | 'append';

export interface FilterIntent {
  filters: ProductFilterPayload;
  mode: FilterMode;
  reason?: FilterChangeReason;
}

export type FilterChangeReason =
  | 'search'
  | 'price'
  | 'sort'
  | 'category'
  | 'store'
  | 'reset'
  | 'branch-change'
  | 'sync';

export interface UseProductFilterEngineOptions {
  debounceMs?: number;
  onFilterChange?: (intent: FilterIntent) => void;
}

export interface UseProductFilterEngineReturn {
  filters: ProductFilterState;
  activeFiltersCount: number;

  updateFilter: <K extends keyof ProductFilterState>(
    key: K,
    value: ProductFilterState[K],
    reason?: FilterChangeReason
  ) => void;

  removeFilter: (key: keyof ProductFilterState) => void;
  resetFilters: () => void;

  updatePriceRange: (min: number, max: number) => void;
  updateSearchQuery: (query: string) => void;

  isFilterActive: (key: keyof ProductFilterState) => boolean;
  getActiveFilters: () => Array<{ key: string; value: any }>;

  priceRange: [number, number];
  searchQuery: string;

  getFilterPayload: () => ProductFilterPayload | null;
  syncFilters: (filters: Partial<ProductFilterState>) => void;
}

// ============================================
// 🧠 HELPERS
// ============================================

const cleanFilters = <T extends object>(filters: T): Partial<T> => {
  const cleaned: Partial<T> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      cleaned[key as keyof T] = value as T[keyof T];
    }
  });
  return cleaned;
};

// ============================================
// 🎯 HOOK
// ============================================

export const useProductFilterEngine = (
  options: UseProductFilterEngineOptions = {}
): UseProductFilterEngineReturn => {
  const { debounceMs = 500, onFilterChange } = options;
  const { selectedBranch } = useShopContext();

  const [filters, setFilters] = useState<ProductFilterState>({});

  // ============================================
  // 🔁 DERIVED UI STATE (NO DUPLICATION)
  // ============================================

  const searchQuery = filters.search ?? '';

  const priceRange: [number, number] = [
    filters.price_min ?? 0,
    filters.price_max ?? 10000
  ];

  // ============================================
  // 🧱 PAYLOAD BUILDER
  // ============================================

  const payload = useMemo<ProductFilterPayload | null>(() => {
    if (!selectedBranch?.id) return null;

    return {
      ...cleanFilters(filters),
      branch: selectedBranch.id.toString()
    };
  }, [filters, selectedBranch]);

  // ============================================
  // ⏳ DEBOUNCED EMIT
  // ============================================

  useEffect(() => {
    if (!payload) return;

    const timer = setTimeout(() => {
      onFilterChange?.({
        filters: payload,
        mode: 'replace'
      });
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [payload, debounceMs, onFilterChange]);

  // ============================================
  // 🎬 ACTIONS
  // ============================================

  const updateFilter = useCallback(
    <K extends keyof ProductFilterState>(
      key: K,
      value: ProductFilterState[K],
      reason: FilterChangeReason = 'sync'
    ) => {
      setFilters(prev => ({
        ...prev,
        [key]: value || undefined
      }));

      if (payload) {
        onFilterChange?.({
          filters: payload,
          mode: 'replace',
          reason
        });
      }
    },
    [payload, onFilterChange]
  );

  const removeFilter = useCallback((key: keyof ProductFilterState) => {
    if (key === 'branch') return;

    setFilters(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetFilters = useCallback(() => {
    if (!selectedBranch?.id) return;

    const reset: ProductFilterState = {
      branch: selectedBranch.id.toString()
    };

    setFilters(reset);

    onFilterChange?.({
      filters: reset as ProductFilterPayload,
      mode: 'replace',
      reason: 'reset'
    });
  }, [selectedBranch, onFilterChange]);

  const updatePriceRange = useCallback((min: number, max: number) => {
    if (min > max) return;

    setFilters(prev => ({
      ...prev,
      price_min: min > 0 ? min : undefined,
      price_max: max < 10000 ? max : undefined
    }));
  }, []);

  const updateSearchQuery = useCallback((query: string) => {
    setFilters(prev => ({
      ...prev,
      search: query || undefined
    }));
  }, []);

  // ============================================
  // 🔍 HELPERS
  // ============================================

  const isFilterActive = useCallback(
    (key: keyof ProductFilterState) =>
      filters[key] !== undefined &&
      filters[key] !== null &&
      filters[key] !== '',
    [filters]
  );

  const getActiveFilters = useCallback(() => {
    return Object.entries(filters)
      .filter(([key, value]) => key !== 'branch' && value != null && value !== '')
      .map(([key, value]) => ({ key, value }));
  }, [filters]);

  const activeFiltersCount = useMemo(() => {
    return getActiveFilters().length;
  }, [getActiveFilters]);

  const getFilterPayload = useCallback(() => payload, [payload]);

  const syncFilters = useCallback(
    (incoming: Partial<ProductFilterState>) => {
      setFilters(prev => ({
        ...cleanFilters(prev),
        ...cleanFilters(incoming),
        branch: selectedBranch?.id?.toString()
      }));

      onFilterChange?.({
        filters: {
          ...cleanFilters(incoming),
          branch: selectedBranch?.id?.toString() || ''
        } as ProductFilterPayload,
        mode: 'replace',
        reason: 'sync'
      });
    },
    [selectedBranch, onFilterChange]
  );

  // ============================================
  // 🧷 BRANCH INJECTION
  // ============================================

  useEffect(() => {
    if (!selectedBranch?.id) return;

    setFilters(prev => {
      if (prev.branch === selectedBranch.id.toString()) return prev;
      return { branch: selectedBranch.id.toString() };
    });

    onFilterChange?.({
      filters: { branch: selectedBranch.id.toString() },
      mode: 'replace',
      reason: 'branch-change'
    });
  }, [selectedBranch, onFilterChange]);

  // ============================================
  // 📤 RETURN
  // ============================================

  return {
    filters,
    activeFiltersCount,

    updateFilter,
    removeFilter,
    resetFilters,

    updatePriceRange,
    updateSearchQuery,

    isFilterActive,
    getActiveFilters,

    priceRange,
    searchQuery,

    getFilterPayload,
    syncFilters
  };
};
